import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../redux/hooks'
import companyPlanningRecordActions from '../../redux/modules/companyPlanningRecord/actions'
import companyPlanningRecordSelectors from '../../redux/modules/companyPlanningRecord/selectors'
import authSelectors from '../../redux/modules/auth/selectors'
import { useIsMountedRef } from '../shared/useIsMountedRef'
import { useUnsubscribe } from '../shared/useUnsubscribe'
import { CompanyPlanningRecord } from '@leadgen/models/client'
import useStableCallback from '../shared/useStableCallback'

export const useCompanyPlanningRecord = ({
  companyPlanningRecordId,
  onLoadComplete,
}: {
  companyPlanningRecordId: string
  onLoadComplete?: (companyPlanningRecord: CompanyPlanningRecord) => void
}) => {
  const stableOnLoadComplete = useStableCallback((companyPlanningRecord: CompanyPlanningRecord) => {
    if (onLoadComplete) {
      onLoadComplete(companyPlanningRecord)
    }
  })
  const companyId = useSelector(authSelectors.getCompanyId)
  const isMountedRef = useIsMountedRef()
  const fetchingCompanyPlanningRecordIdRef = useRef<string>()
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const dispatch = useDispatch()
  const companyPlanningRecord = useSelector(
    companyPlanningRecordSelectors.getCompanyPlanningRecord({ companyPlanningRecordId }),
  )
  const [loading, loadingRef] = useSelectorWithRef(companyPlanningRecordSelectors.getLoading())
  const [, initialRef] = useSelectorWithRef(companyPlanningRecordSelectors.getInitial)
  const error = useSelector(companyPlanningRecordSelectors.getError)
  const load = useCallback(() => {
    if (
      companyId &&
      (fetchingCompanyPlanningRecordIdRef.current !== companyPlanningRecordId ||
        !loadingRef.current ||
        initialRef.current)
    ) {
      fetchingCompanyPlanningRecordIdRef.current = companyPlanningRecordId
      unsubscribe()
      dispatch(
        companyPlanningRecordActions.loadCompanyPlanningRecord({
          companyPlanningRecordId,
          companyId,
        }),
      )
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromCompanyPlanningRecord }) => {
          if (fetchingCompanyPlanningRecordIdRef.current !== companyPlanningRecordId) {
            unsubscribeFromCompanyPlanningRecord()
          }
          if (isMountedRef.current === false) {
            if (fetchingCompanyPlanningRecordIdRef.current == companyPlanningRecordId) {
              unsubscribeFromCompanyPlanningRecord()
            }
            unsubscribe()
          } else {
            stableOnLoadComplete(data)
            unsubscribe()
            registerUnsubscribe(unsubscribeFromCompanyPlanningRecord)
          }
        })
    }
  }, [
    stableOnLoadComplete,
    companyId,
    dispatch,
    isMountedRef,
    loadingRef,
    initialRef,
    registerUnsubscribe,
    unsubscribe,
    companyPlanningRecordId,
  ])
  useEffect(() => {
    load()
    return () => {
      unsubscribe()
    }
  }, [load, unsubscribe])
  return {
    companyPlanningRecord,
    load,
    loading,
    error,
  }
}
